import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {UserCircle, Lock, Eye, EyeOff, Loader2, ArrowLeft, ArrowRight, TreePine, CheckCircle, Clock, XCircle, AlertCircle, Leaf, MapPin, Calendar, FileText, LogOut, RefreshCw, Sprout, ChevronRight, Info, AlertTriangle, KeyRound, ShieldCheck, ImageIcon, X} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import {
  loginCidadao, salvarSessaoCidadao, lerSessaoCidadao,
  limparSessaoCidadao, normalizarCPF, alterarSenhaCidadao,
  senhaDosCPF, type CidadaoSession
} from '@/utils/cidadaoAuth'

// ── Paleta ──────────────────────────────────────────────────────────────────
const BRAND   = '#0f3b50'
const GREEN   = '#3a7d2c'

// ── Formatadores ─────────────────────────────────────────────────────────────
function fmtCPF(v: string) {
  const d = v.replace(/\D/g, '').slice(0, 11)
  return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
         .replace(/(\d{3})(\d{3})(\d{3})$/, '$1.$2.$3')
         .replace(/(\d{3})(\d{3})$/, '$1.$2')
}
function fmtData(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pt-BR')
}
function fmtDataHora(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  })
}
function priNome(nome: string) {
  return nome?.split(' ')[0] || nome
}

// ── Mapa de cores de status ──────────────────────────────────────────────────
const STATUS_MAP: Record<string, { bg: string; text: string; border: string; icon: React.ElementType }> = {
  'Solicitação Recebida':   { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe', icon: Clock },
  'Em Análise':             { bg: '#fefce8', text: '#854d0e', border: '#fde68a', icon: Clock },
  'Vistoria Agendada':      { bg: '#faf5ff', text: '#6b21a8', border: '#e9d5ff', icon: Calendar },
  'Vistoria Realizada':     { bg: '#eef2ff', text: '#3730a3', border: '#c7d2fe', icon: CheckCircle },
  'Aprovada':               { bg: '#f0fdf4', text: '#166534', border: '#bbf7d0', icon: CheckCircle },
  'Reprovada':              { bg: '#fef2f2', text: '#991b1b', border: '#fecaca', icon: XCircle },
  'Aguardando Replantio':   { bg: '#fffbeb', text: '#92400e', border: '#fcd34d', icon: AlertCircle },
  'Processo Concluído':     { bg: '#f0fdf4', text: '#065f46', border: '#6ee7b7', icon: CheckCircle },
}

function StatusBadge({ status }: { status?: string }) {
  const s = status || ''
  const st = STATUS_MAP[s] || { bg: '#f3f4f6', text: '#374151', border: '#d1d5db', icon: Clock }
  const Icon = st.icon
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border"
      style={{ backgroundColor: st.bg, color: st.text, borderColor: st.border }}
    >
      <Icon className="w-3 h-3" />
      {s || 'Sem status'}
    </span>
  )
}

// ── Tela de Login do Cidadão ──────────────────────────────────────────────────
function TelaLogin({ onLogin }: { onLogin: (s: CidadaoSession) => void }) {
  const navigate = useNavigate()
  const [cpf,       setCpf]       = useState('')
  const [senha,     setSenha]     = useState('')
  const [showSenha, setShowSenha] = useState(false)
  const [loading,   setLoading]   = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const cpfLimpo = normalizarCPF(cpf)
    if (cpfLimpo.length < 11) { toast.error('CPF inválido. Verifique e tente novamente.'); return }
    if (!senha.trim())        { toast.error('Informe a senha (6 primeiros dígitos do CPF).'); return }

    setLoading(true)
    try {
      const sessao = await loginCidadao(cpfLimpo, senha.trim())
      if (!sessao) {
        toast.error('CPF ou senha incorretos. A senha são os 6 primeiros dígitos do seu CPF.')
        return
      }
      salvarSessaoCidadao(sessao)
      toast.success(`Bem-vindo, ${priNome(sessao.nomeCompleto)}!`)
      onLogin(sessao)
    } catch {
      toast.error('Erro ao entrar. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12"
      style={{ background: `linear-gradient(145deg, #0a2a38 0%, ${BRAND} 60%, #1a5c40 100%)` }}
    >
      {/* Botão voltar */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-5 left-5 flex items-center gap-2 text-white/70 hover:text-white text-sm transition"
      >
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>

      {/* Card principal */}
      <div className="w-full max-w-md">
        {/* Logo e título */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-20 h-20 rounded-3xl flex items-center justify-center mb-4 shadow-2xl"
            style={{ background: 'rgba(255,255,255,0.12)', border: '2px solid rgba(255,255,255,0.2)' }}
          >
            <UserCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-extrabold text-white tracking-tight">Portal do Cidadão</h1>
          <p className="text-white/60 text-sm mt-1 text-center">
            Acompanhe os seus processos de remoção de árvores
          </p>
          <div className="flex items-center gap-2 mt-3 bg-white/10 border border-white/15 rounded-full px-4 py-1.5">
            <Leaf className="w-3 h-3 text-green-300" />
            <span className="text-green-200 text-xs font-medium">Prefeitura de São José do Seridó/RN</span>
          </div>
        </div>

        {/* Formulário */}
        <div className="bg-white rounded-3xl shadow-2xl p-7">
          {/* Dica de acesso */}
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-2xl px-4 py-3 mb-6">
            <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-800 text-xs font-semibold">Como aceder?</p>
              <p className="text-blue-700 text-xs mt-0.5 leading-relaxed">
                O seu <strong>CPF</strong> é o utilizador e a <strong>senha são os 6 primeiros dígitos</strong> do seu CPF.<br />
                Exemplo: CPF <code className="bg-blue-100 px-1 rounded">123.456.789-00</code> → senha <code className="bg-blue-100 px-1 rounded">123456</code>
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* CPF */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                CPF
              </label>
              <div className="relative">
                <UserCircle className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  inputMode="numeric"
                  value={cpf}
                  onChange={e => setCpf(fmtCPF(e.target.value))}
                  placeholder="000.000.000-00"
                  style={{ fontSize: '16px' }}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-2xl text-sm outline-none transition bg-gray-50/50 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                />
              </div>
            </div>

            {/* Senha */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Senha (6 primeiros dígitos do CPF)
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type={showSenha ? 'text' : 'password'}
                  inputMode="numeric"
                  value={senha}
                  onChange={e => setSenha(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6 dígitos"
                  maxLength={6}
                  style={{ fontSize: '16px' }}
                  className="w-full pl-10 pr-11 py-3 border border-gray-200 rounded-2xl text-sm outline-none transition bg-gray-50/50 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                />
                <button type="button" onClick={() => setShowSenha(v => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full text-white font-bold py-3.5 rounded-2xl transition-all flex items-center justify-center gap-2 shadow-sm mt-2 disabled:opacity-60"
              style={{ backgroundColor: BRAND }}
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> A entrar...</> : <><span>Aceder ao Portal</span><ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>

        <p className="text-center text-white/40 text-xs mt-6">
          © {new Date().getFullYear()} GEOA – Gestão Online Ambiental
        </p>
      </div>
    </div>
  )
}

// ── Portal autenticado ────────────────────────────────────────────────────────
type Solicitacao = Record<string, any>
type Historico   = Record<string, any>

// ── Modal de Alterar Senha ───────────────────────────────────────────────────
function ModalAlterarSenha({ cpf, onClose }: { cpf: string; onClose: () => void }) {
  const senhaDefault = senhaDosCPF(normalizarCPF(cpf))
  const [novaSenha,    setNovaSenha]    = useState('')
  const [confirmar,    setConfirmar]    = useState('')
  const [showNova,     setShowNova]     = useState(false)
  const [showConf,     setShowConf]     = useState(false)
  const [salvando,     setSalvando]     = useState(false)
  const [sucesso,      setSucesso]      = useState(false)

  const handleSalvar = async (e: React.FormEvent) => {
    e.preventDefault()
    if (novaSenha.length < 4) { toast.error('A senha deve ter pelo menos 4 caracteres.'); return }
    if (novaSenha !== confirmar) { toast.error('As senhas não coincidem.'); return }
    setSalvando(true)
    try {
      await alterarSenhaCidadao(cpf, novaSenha)
      setSucesso(true)
      toast.success('Senha alterada com sucesso!')
    } catch (err) {
      toast.error('Erro ao alterar senha. Tente novamente.')
      console.error(err)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-center justify-between" style={{ backgroundColor: BRAND }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-2xl bg-white/15 flex items-center justify-center">
              <KeyRound className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-white/60 text-xs">Portal do Cidadão</p>
              <p className="text-white font-bold text-sm">Alterar Senha</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition">✕</button>
        </div>

        <div className="p-6">
          {sucesso ? (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="w-16 h-16 rounded-3xl bg-green-50 flex items-center justify-center">
                <ShieldCheck className="w-8 h-8 text-green-600" />
              </div>
              <div className="text-center">
                <p className="font-bold text-gray-800 text-base">Senha alterada com sucesso!</p>
                <p className="text-sm text-gray-500 mt-1">Use a nova senha no próximo acesso ao Portal do Cidadão.</p>
              </div>
              <div className="w-full bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">
                <p className="text-xs text-amber-700 font-semibold text-center">
                  ⚠️ Guarde bem a sua nova senha. Em caso de esquecimento, contacte a Prefeitura.
                </p>
              </div>
              <button onClick={onClose}
                className="w-full py-3 rounded-2xl text-white font-bold transition"
                style={{ backgroundColor: BRAND }}
              >Fechar</button>
            </div>
          ) : (
            <form onSubmit={handleSalvar} className="space-y-4">
              {/* Info senha padrão */}
              <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-2xl px-4 py-3">
                <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-blue-700 text-xs leading-relaxed">
                  A sua senha padrão atual é <code className="bg-blue-100 px-1.5 py-0.5 rounded font-mono font-bold">{senhaDefault}</code> (6 primeiros dígitos do CPF).
                  Pode criar uma senha personalizada abaixo.
                </p>
              </div>

              {/* Nova senha */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Nova Senha</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type={showNova ? 'text' : 'password'}
                    value={novaSenha}
                    onChange={e => setNovaSenha(e.target.value)}
                    placeholder="Mínimo 4 caracteres"
                    style={{ fontSize: '16px' }}
                    className="w-full pl-10 pr-11 py-3 border border-gray-200 rounded-2xl text-sm outline-none transition bg-gray-50/50 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                  />
                  <button type="button" onClick={() => setShowNova(v => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                    {showNova ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Confirmar */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Confirmar Nova Senha</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type={showConf ? 'text' : 'password'}
                    value={confirmar}
                    onChange={e => setConfirmar(e.target.value)}
                    placeholder="Repita a nova senha"
                    style={{ fontSize: '16px' }}
                    className="w-full pl-10 pr-11 py-3 border border-gray-200 rounded-2xl text-sm outline-none transition bg-gray-50/50 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                  />
                  <button type="button" onClick={() => setShowConf(v => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                    {showConf ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {confirmar && novaSenha && confirmar !== novaSenha && (
                  <p className="text-red-500 text-xs mt-1">As senhas não coincidem.</p>
                )}
              </div>

              <button
                type="submit"
                disabled={salvando}
                className="w-full py-3.5 rounded-2xl text-white font-bold transition flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ backgroundColor: BRAND }}
              >
                {salvando ? <><Loader2 className="w-4 h-4 animate-spin" />A guardar...</> : <><KeyRound className="w-4 h-4" />Salvar Nova Senha</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

function PortalAutenticado({ sessao, onLogout }: { sessao: CidadaoSession; onLogout: () => void }) {
  const [solicitacoes, setSolicitacoes] = useState<Solicitacao[]>([])
  const [historicos,   setHistoricos]   = useState<Record<string, Historico[]>>({})
  const [loading,      setLoading]      = useState(true)
  const [selecionada,  setSelecionada]  = useState<Solicitacao | null>(null)
  const [aba,          setAba]          = useState<'abertas' | 'concluidas'>('abertas')
  const [modalSenha,   setModalSenha]   = useState(false)

  const cpfLimpo = normalizarCPF(sessao.cpf)

  const carregar = async () => {
    setLoading(true)
    try {
      const res = await lumi.entities.solicitacoes.list({
        sort: { createdAt: -1 },
        limit: 200,
      })
      const todas = (res?.list || []) as Solicitacao[]
      const minhas = todas.filter(s => normalizarCPF(s.cpf || '') === cpfLimpo)
      setSolicitacoes(minhas)
    } catch {
      toast.error('Erro ao carregar solicitações.')
    } finally {
      setLoading(false)
    }
  }

  const carregarHistorico = async (s: Solicitacao) => {
    setSelecionada(s)
    if (historicos[s._id]) return
    try {
      const res = await lumi.entities.historico_status.list({
        filter: { solicitacaoId: s._id },
        sort: { createdAt: 1 },
        limit: 50,
      })
      setHistoricos(prev => ({ ...prev, [s._id]: (res?.list || []) as Historico[] }))
    } catch { /* silencioso */ }
  }

  useEffect(() => { carregar() }, [])

  const abertas    = solicitacoes.filter(s => !s.finalizado && s.status !== 'Processo Concluído')
  const concluidas = solicitacoes.filter(s => s.finalizado || s.status === 'Processo Concluído')

  // ── Estatísticas globais ─────────────────────────────────────────────────
  const totalArvoresRemovidasPedidas = solicitacoes.reduce((acc, s) => acc + (Number(s.quantidadeArvores) || 0), 0)
  const totalPlantadas = concluidas.filter(s => s.arvoreReplantada || s.seraSubstituida).length
  const dentroDoPrezo  = concluidas.filter(s => s.dentroDoPrezo === true).length

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Header ── */}
      <header className="sticky top-0 z-30 shadow-sm" style={{ backgroundColor: BRAND }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-2xl bg-white/15 flex items-center justify-center flex-shrink-0">
              <UserCircle className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-white/60 text-xs">Portal do Cidadão</p>
              <p className="text-white font-bold text-sm truncate">
                Olá, {priNome(sessao.nomeCompleto)}! 👋
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={carregar} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition text-white" title="Atualizar">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={() => setModalSenha(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition text-white text-xs font-semibold"
              title="Alterar senha"
            >
              <KeyRound className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Alterar Senha</span>
            </button>
            <button onClick={onLogout} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition text-white text-xs font-semibold">
              <LogOut className="w-3.5 h-3.5" /> Sair
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* ── Boas-vindas + nome completo ── */}
        <div
          className="rounded-3xl p-6 shadow-sm"
          style={{ background: `linear-gradient(135deg, ${BRAND} 0%, #1a5873 100%)` }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center flex-shrink-0">
              <UserCircle className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white/60 text-xs uppercase tracking-wide font-semibold">Bem-vindo(a)</p>
              <h2 className="text-white text-xl font-extrabold leading-tight truncate">{sessao.nomeCompleto}</h2>
              <p className="text-white/50 text-xs mt-0.5 font-mono">
                CPF: {fmtCPF(sessao.cpf)}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className="text-white/40 text-xs">Total de processos</span>
              <span className="text-white text-3xl font-black">{solicitacoes.length}</span>
            </div>
          </div>
        </div>

        {/* ── Cards de estatísticas ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Em curso',       value: abertas.length,                   color: '#1d4ed8', bg: '#eff6ff', icon: Clock },
            { label: 'Concluídos',     value: concluidas.length,                color: '#065f46', bg: '#f0fdf4', icon: CheckCircle },
            { label: 'Árvores pedidas', value: totalArvoresRemovidasPedidas,    color: '#92400e', bg: '#fffbeb', icon: TreePine },
            { label: 'Replantios',     value: totalPlantadas,                   color: '#166534', bg: '#f0fdf4', icon: Sprout },
          ].map(({ label, value, color, bg, icon: Icon }) => (
            <div key={label} className="rounded-2xl p-4 shadow-sm border border-gray-100" style={{ backgroundColor: bg }}>
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4" style={{ color }} />
                <span className="text-xs font-semibold" style={{ color }}>{label}</span>
              </div>
              <p className="text-2xl font-black" style={{ color }}>{value}</p>
            </div>
          ))}
        </div>

        {/* ── Aviso replantio dentro do prazo ── */}
        {concluidas.length > 0 && (
          <div className="rounded-2xl p-4 flex items-start gap-3 border"
            style={{ backgroundColor: dentroDoPrezo === concluidas.length ? '#f0fdf4' : '#fffbeb',
                     borderColor: dentroDoPrezo === concluidas.length ? '#bbf7d0' : '#fcd34d' }}
          >
            {dentroDoPrezo === concluidas.length
              ? <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              : <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            }
            <div>
              <p className="text-sm font-semibold"
                style={{ color: dentroDoPrezo === concluidas.length ? '#166534' : '#92400e' }}>
                {dentroDoPrezo === concluidas.length
                  ? 'Todos os replantios dentro do prazo'
                  : 'Atenção: verifique os prazos de replantio'}
              </p>
              <p className="text-xs mt-0.5 text-gray-600">
                Após autorização, o replantio deve ser feito em até <strong>15 dias</strong> conforme a Lei nº 426/2018.
                {dentroDoPrezo < concluidas.length && ` ${concluidas.length - dentroDoPrezo} processo(s) podem ter excedido o prazo.`}
              </p>
            </div>
          </div>
        )}

        {/* ── Abas ── */}
        <div>
          <div className="flex gap-1 bg-gray-100 rounded-2xl p-1 w-fit mb-4">
            {(['abertas', 'concluidas'] as const).map(a => (
              <button key={a} onClick={() => setAba(a)}
                className="px-5 py-2 rounded-xl text-sm font-semibold transition-all"
                style={aba === a
                  ? { backgroundColor: BRAND, color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.15)' }
                  : { color: '#6b7280' }
                }
              >
                {a === 'abertas'
                  ? `Em curso (${abertas.length})`
                  : `Concluídos (${concluidas.length})`}
              </button>
            ))}
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: BRAND }} />
              <p className="text-gray-500 text-sm">A carregar processos...</p>
            </div>
          )}

          {/* Lista de processos */}
          {!loading && (
            <div className="space-y-3">
              {(aba === 'abertas' ? abertas : concluidas).map(s => (
                <div key={s._id}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-blue-100 transition-all cursor-pointer"
                  onClick={() => carregarHistorico(s)}
                >
                  <div className="p-4 flex items-start gap-4">
                    {/* Ícone */}
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: s.finalizado || s.status === 'Processo Concluído' ? '#f0fdf4' : '#eff6ff' }}
                    >
                      {s.finalizado || s.status === 'Processo Concluído'
                        ? <CheckCircle className="w-5 h-5 text-green-600" />
                        : <TreePine className="w-5 h-5 text-blue-600" />
                      }
                    </div>

                    {/* Conteúdo */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 mb-2">
                        <span className="font-mono font-bold text-sm text-gray-800 truncate">{s.protocolo}</span>
                        <StatusBadge status={s.status} />
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <TreePine className="w-3 h-3" />
                          <span className="truncate">{s.tipoArvore || '—'} ({s.quantidadeArvores || 1})</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{s.ruaNome || s.bairroNome || s.endereco || '—'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>{fmtData(s.createdAt)}</span>
                        </div>
                      </div>

                      {/* Replantio (concluídos) */}
                      {(s.finalizado || s.status === 'Processo Concluído') && s.arvoreReplantada && (
                        <div className="mt-2 flex items-center gap-1.5 bg-green-50 border border-green-100 rounded-xl px-3 py-1.5">
                          <Sprout className="w-3.5 h-3.5 text-green-600" />
                          <span className="text-xs text-green-700 font-medium">
                            Replantada: {s.arvoreReplantada}
                            {s.tipoArvoreReplantada && ` (${s.tipoArvoreReplantada})`}
                            {s.dentroDoPrezo === true && <span className="ml-1.5 text-green-600">✓ dentro do prazo</span>}
                            {s.dentroDoPrezo === false && <span className="ml-1.5 text-amber-600">⚠ fora do prazo</span>}
                          </span>
                        </div>
                      )}
                    </div>

                    <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0 mt-1" />
                  </div>
                </div>
              ))}

              {/* Vazio */}
              {(aba === 'abertas' ? abertas : concluidas).length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                  <div className="w-16 h-16 rounded-3xl flex items-center justify-center mb-2"
                    style={{ backgroundColor: '#f0fdf4' }}
                  >
                    {aba === 'abertas' ? <Clock className="w-8 h-8 text-green-400" /> : <CheckCircle className="w-8 h-8 text-green-400" />}
                  </div>
                  <p className="text-gray-500 font-semibold text-sm">
                    {aba === 'abertas' ? 'Nenhum processo em curso' : 'Nenhum processo concluído'}
                  </p>
                  <p className="text-gray-400 text-xs max-w-xs">
                    {aba === 'abertas'
                      ? 'Os processos em aberto e em análise aparecem aqui.'
                      : 'Quando um processo for finalizado, aparecerá aqui com todos os detalhes.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Modal de Alterar Senha ── */}
      {modalSenha && (
        <ModalAlterarSenha
          cpf={sessao.cpf}
          onClose={() => setModalSenha(false)}
        />
      )}

      {/* ── Modal de detalhe ── */}
      {selecionada && (
        <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto flex items-start justify-center py-6 px-4"
          onClick={e => { if (e.target === e.currentTarget) setSelecionada(null) }}
        >
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl relative">
            {/* Cabeçalho do modal */}
            <div className="rounded-t-3xl px-6 pt-6 pb-4 flex items-start justify-between gap-4"
              style={{ backgroundColor: BRAND }}
            >
              <div>
                <p className="text-white/60 text-xs uppercase tracking-wide font-semibold">Processo</p>
                <p className="text-white font-mono font-bold text-base">{selecionada.protocolo}</p>
                <div className="mt-2"><StatusBadge status={selecionada.status} /></div>
              </div>
              <button onClick={() => setSelecionada(null)}
                className="w-8 h-8 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition flex-shrink-0"
              >✕</button>
            </div>

            <div className="p-6 space-y-5">
              {/* Informações do processo */}
              <Section title="Dados da Solicitação" icon={FileText}>
                <Grid2>
                  <Field label="Solicitante"   value={selecionada.nomeCompleto} />
                  <Field label="CPF"            value={fmtCPF(selecionada.cpf || '')} />
                  <Field label="Data de Abertura" value={fmtData(selecionada.createdAt)} />
                  <Field label="Origem"         value={selecionada.origem === 'online' ? 'Portal Público' : 'Interno'} />
                  <Field label="Tipo de Árvore" value={selecionada.tipoArvore} />
                  <Field label="Quantidade"     value={selecionada.quantidadeArvores} />
                  <Field label="Porte"          value={selecionada.porte} />
                  <Field label="Estado"         value={selecionada.estadoArvore} />
                  {selecionada.coordenadas && (
                    <Field label="Coordenadas GPS" value={selecionada.coordenadas} />
                  )}
                </Grid2>
                <Field label="Localização" value={[selecionada.ruaNome, selecionada.numero, selecionada.bairroNome, selecionada.pontoReferencia].filter(Boolean).join(', ')} />
                {selecionada.relatoProblema && (
                  <Field label="Relato do Problema" value={selecionada.relatoProblema} />
                )}
              </Section>

              {/* Replantio registado pelo admin */}
              {selecionada.replantioRegistado && (
                <Section title="Replantio Efectuado" icon={Sprout}>
                  <Grid2>
                    <Field label="Tipo / Espécie Plantada" value={selecionada.replantioTipoArvore} />
                    <Field label="Data do Plantio"          value={selecionada.replantioData ? new Date(selecionada.replantioData).toLocaleDateString('pt-BR') : selecionada.replantioData} />
                    <Field label="Responsável pelo Plantio" value={selecionada.replantioResponsavel} />
                    <Field label="Local do Replantio"       value={selecionada.replantioEndereco} />
                  </Grid2>

                  {/* Galeria de fotos */}
                  {selecionada.replantioFotos && selecionada.replantioFotos.length > 0 && (
                    <GaleriaReplantio fotos={selecionada.replantioFotos} />
                  )}
                </Section>
              )}

              {/* Replantio (se concluído) */}
              {(selecionada.finalizado || selecionada.status === 'Processo Concluído') && (
                <Section title="Dados do Replantio" icon={Sprout}>
                  <Grid2>
                    <Field label="Árvore Plantada"    value={selecionada.arvoreReplantada || (selecionada.seraSubstituida ? 'Sim' : 'Não informado')} />
                    <Field label="Tipo/Espécie"        value={selecionada.tipoArvoreReplantada} />
                    <Field label="Local do Replantio"  value={selecionada.enderecoReplantio || selecionada.ondeReplantio} />
                    <Field label="Data de Conclusão"   value={fmtData(selecionada.dataFinalizacao)} />
                  </Grid2>
                  <div className="mt-2 flex items-center gap-2 rounded-xl px-4 py-2.5 border"
                    style={selecionada.dentroDoPrezo === true
                      ? { backgroundColor: '#f0fdf4', borderColor: '#bbf7d0' }
                      : selecionada.dentroDoPrezo === false
                        ? { backgroundColor: '#fffbeb', borderColor: '#fcd34d' }
                        : { backgroundColor: '#f9fafb', borderColor: '#e5e7eb' }
                    }
                  >
                    {selecionada.dentroDoPrezo === true
                      ? <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      : selecionada.dentroDoPrezo === false
                        ? <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        : <Info className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    }
                    <span className="text-sm font-medium"
                      style={{ color: selecionada.dentroDoPrezo === true ? '#166534' : selecionada.dentroDoPrezo === false ? '#92400e' : '#6b7280' }}
                    >
                      {selecionada.dentroDoPrezo === true
                        ? 'Replantio realizado dentro do prazo de 15 dias ✓'
                        : selecionada.dentroDoPrezo === false
                          ? 'Replantio realizado fora do prazo — verificar penalização'
                          : 'Prazo de replantio não registado'}
                    </span>
                  </div>
                  {selecionada.observacaoFinalizacao && (
                    <Field label="Observação de Conclusão" value={selecionada.observacaoFinalizacao} />
                  )}
                </Section>
              )}

              {/* Histórico */}
              <Section title="Histórico de Atualizações" icon={Clock}>
                {!historicos[selecionada._id] ? (
                  <div className="flex items-center gap-2 text-gray-400 text-sm py-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> A carregar histórico...
                  </div>
                ) : historicos[selecionada._id].length === 0 ? (
                  <p className="text-gray-400 text-sm py-2">Nenhuma atualização registada.</p>
                ) : (
                  <div className="relative pl-4">
                    {/* Linha vertical */}
                    <div className="absolute left-1.5 top-2 bottom-2 w-px bg-gray-200" />
                    <div className="space-y-4">
                      {historicos[selecionada._id].map((h, i) => {
                        const st = STATUS_MAP[h.status] || { bg: '#f3f4f6', text: '#374151', border: '#d1d5db', icon: Clock }
                        const Ic = st.icon
                        return (
                          <div key={i} className="relative pl-5">
                            {/* Ponto na linha */}
                            <div className="absolute left-[-7px] top-1.5 w-3.5 h-3.5 rounded-full border-2 border-white"
                              style={{ backgroundColor: st.text }}
                            />
                            <div className="bg-gray-50 rounded-2xl border border-gray-100 p-3.5">
                              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-0.5 rounded-full border"
                                  style={{ backgroundColor: st.bg, color: st.text, borderColor: st.border }}
                                >
                                  <Ic className="w-3 h-3" />{h.status}
                                </span>
                                <span className="text-gray-400 text-xs">{fmtDataHora(h.createdAt)}</span>
                              </div>
                              {h.responsavel && (
                                <p className="text-xs text-gray-500 mb-1">
                                  Responsável: <span className="font-semibold text-gray-700">{h.responsavel}</span>
                                </p>
                              )}
                              {(h.observacoes || h.observacao) && (
                                <p className="text-sm text-gray-700 italic leading-relaxed border-l-2 pl-2.5 mt-1"
                                  style={{ borderColor: st.text + '66' }}
                                >
                                  "{h.observacoes || h.observacao}"
                                </p>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </Section>

              {/* PDF assinado */}
              {selecionada.pdfAssinadoUrl && (
                <a
                  href={selecionada.pdfAssinadoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 w-full px-5 py-3.5 rounded-2xl border transition-all group hover:shadow-md"
                  style={{ borderColor: '#bbf7d0', backgroundColor: '#f0fdf4' }}
                >
                  <FileText className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-green-800 font-semibold text-sm">Documento Assinado Disponível</p>
                    <p className="text-green-600 text-xs mt-0.5">Clique para descarregar o PDF oficial do processo</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-green-600 transition-transform group-hover:translate-x-0.5" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Galeria de fotos de replantio ─────────────────────────────────────────────
function GaleriaReplantio({ fotos }: { fotos: string[] }) {
  const [preview, setPreview] = useState<string | null>(null)
  return (
    <div className="mt-3">
      <p className="text-[10px] uppercase tracking-wide font-semibold text-gray-400 mb-2">Fotos do Replantio</p>
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${Math.min(fotos.length, 3)}, 1fr)` }}
      >
        {fotos.map((url, i) => (
          <div
            key={i}
            className="relative rounded-2xl overflow-hidden border border-gray-100 cursor-pointer group"
            style={{ aspectRatio: '1/1' }}
            onClick={() => setPreview(url)}
          >
            <img src={url} alt={`Replantio ${i + 1}`} className="w-full h-full object-cover transition group-hover:scale-105" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition flex items-center justify-center">
              <ImageIcon className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition" />
            </div>
          </div>
        ))}
      </div>
      {preview && (
        <div
          className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center"
          onClick={() => setPreview(null)}
        >
          <img src={preview} alt="Preview" className="max-w-[90vw] max-h-[85vh] rounded-2xl shadow-2xl" />
          <button
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white"
            onClick={e => { e.stopPropagation(); setPreview(null) }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  )
}

// ── Componentes auxiliares ────────────────────────────────────────────────────
function Section({ title, icon: Icon, children }: { title: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: BRAND + '18' }}>
          <Icon className="w-3.5 h-3.5" style={{ color: BRAND }} />
        </div>
        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">{title}</h3>
      </div>
      <div className="bg-gray-50 rounded-2xl p-4 space-y-3 border border-gray-100">{children}</div>
    </div>
  )
}
function Grid2({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-3">{children}</div>
}
function Field({ label, value }: { label: string; value?: any }) {
  if (!value && value !== 0) return null
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide font-semibold text-gray-400">{label}</p>
      <p className="text-sm text-gray-800 font-medium mt-0.5 leading-snug">{String(value)}</p>
    </div>
  )
}

// ── Componente raiz ───────────────────────────────────────────────────────────
export default function PortalCidadaoPage() {
  const [sessao, setSessao] = useState<CidadaoSession | null>(() => lerSessaoCidadao())

  const handleLogin = (s: CidadaoSession) => {
    salvarSessaoCidadao(s)
    setSessao(s)
  }

  const handleLogout = () => {
    limparSessaoCidadao()
    setSessao(null)
    toast.success('Sessão encerrada.')
  }

  if (!sessao) return <TelaLogin onLogin={handleLogin} />
  return <PortalAutenticado sessao={sessao} onLogout={handleLogout} />
}
